import { leapCrossesStasis } from "./stasisLeap";
import {
  BISHOP,
  BISHOP_DIRS,
  CASTLE_BK,
  CASTLE_BQ,
  CASTLE_WK,
  CASTLE_WQ,
  DIR_OFFSET,
  forwardFacing,
  KING,
  KING_DIRS,
  KNIGHT,
  KNIGHT_DIRS,
  PAWN,
  PAWN_CAPTURES_BY_FACING,
  pawnStartRank,
  pieceColor,
  pieceType,
  QUEEN,
  QUEEN_DIRS,
  rankOf,
  ROOK,
  ROOK_DIRS,
  type Piece,
  type PieceType,
  type Side,
  square,
  stepOffset,
  WHITE} from "../board";
import {
  createMove,
  FLAG_CAPTURE,
  FLAG_CASTLE,
  FLAG_DOUBLE,
  FLAG_BASH,
  FLAG_EP,
  isAttack,
  isBash,
  isDrag,
  type Move,
  moveOnto,
  moveOntoStructure,
  NO_BREAK,
  breaksStructures,
  isStructureBlow
} from "../move";
import {
  areEnemies,
  noBuildingFor,
  armourLeft,
  bindingRoyal,
  claspedTo,
  disguisedAs,
  facingAt,
  hasCell,
  crownedFrom,
  imitatingAt,
  isLastRoyal,
  kingReach,
  moatLeft,
  moveStamp,
  obstructsAt,
  blowOf,
  structureHealthAt,
  pieceAt,
  type Position,
  promotesOn,
  variantAt,
} from "../position";
import { isAttackedByEnemyOf, pinnedAgainst } from "./attacks";
import { claspMove } from "./clasp";
import { makeMove, unmakeMove } from "./makeMove";
import {
  pieceVariant,
  SLIDER_RANGE,
  variantExtraSteps,
  variantLeaps,
  variantRange,
  VARIANT_OFFSETS
} from "../rules";
import { blowKills, blowOnto, blowOntoStructure } from "./blow";
import { crushes, enterCost, extend, plowed, WALL, withinBudget } from "./plow";

const PROMOTION_TYPES = [QUEEN, ROOK, BISHOP, KNIGHT] as const;

/**
 * How empty a board has to be before generation walks the pieces instead of
 * the ground.
 *
 * Four cells to a piece. The classic board is two, so it keeps sweeping
 * `cellList` and **perft is untouched by construction** — which matters more
 * than the constant being optimal, since perft is the oracle and it lives
 * almost entirely on 8x8. A grown run is the other case: 3,136 cells and 94
 * men is thirty-three, and generation was walking every one of those cells to
 * find them.
 */
const PIECE_SCAN_RATIO = 4;

/**
 * The squares generation walks, in board order.
 *
 * **Order is the whole difficulty.** Move order feeds every tie-break the AI
 * and the industry rules make, so it has to be a property of the board rather
 * than of how the pieces got there — and `board` is a Map, whose order is
 * insertion order, which make/unmake churns. Sorting the occupied squares
 * gives exactly the sequence sweeping `cellList` gives, since a packed square
 * is `rank << SHIFT | file` and `cellList` is sorted: the same moves in the
 * same order, from a list thirty times shorter on a grown board.
 *
 * `cellList` rather than the bounding rectangle for the dense case: the board
 * need not be a rectangle, and sweeping the box would walk the holes as well —
 * O(area) instead of O(board).
 *
 * Cached on the position beside `moveCache`, so a sort is paid once per board
 * rather than once per generation, and thrown away by the same
 * `invalidateMoves` every mutator already calls.
 */
export function scanSquares(pos: Position): readonly number[] {
  if (pos.cells.size <= pos.board.size * PIECE_SCAN_RATIO) return pos.cellList;
  let list = pos.pieceSquares;
  if (list === undefined) {
    list = [...pos.board.keys()].sort((a, b) => a - b);
    pos.pieceSquares = list;
  }
  return list;
}

/**
 * Pseudo-legal moves for the side to move, written into `out` from `start`.
 * Returns the end index. "Pseudo-legal" means it may leave the king in check;
 * `generateLegalMoves` filters those out.
 *
 * `capturesOnly` emits the capture subset and nothing else — see
 * `generateLegalCaptures`, which is the only thing that passes it. It changes
 * *what is written down*, never what is looked at: every ray is still swept to
 * its end, so a slider finds the same capture at the same square. Perft never
 * passes it and its counts are therefore untouched by construction.
 */
export function generateMoves(
  pos: Position,
  out: Move[] = [],
  capturesOnly = false
): Move[] {
  const board = pos.board;
  const us = pos.turn;

  const at = (sq: number): number => board.get(sq) ?? 0;

  // Hoisted, so a board with no clamp pays one null check per call rather than
  // one per piece — what keeps perft untouched (§26 C11).
  const clasped = pos.clasp !== null;

  {
    for (const from of scanSquares(pos)) {
      const piece = at(from);
      if (!piece || pieceColor(piece) !== us) continue;

      // Locked by a Stasis Field: the cost of not being takeable, and since §26 C4 often theirs.
      if (armourLeft(pos, from) > 0) continue;

      // A borrowed pattern gives quiet moves; captures come from the real rank.
      // Nothing in `attacks.ts` reads it, so it cannot give check or pin, and
      // nothing writes it since §26 C11 — the pass is the crown's now.
      const borrowed = pos.imitating.size > 0 ? imitatingAt(pos, from) : 0;
      // A crown is not a borrowed pattern: an Empress was a queen, so her old
      // rank grants captures as well as steps. Kept apart so the two stack.
      const crown = crownedFrom(pos, from);

      // A clamped piece has its list filtered after emission (§26 C11): a move
      // whose drag has nowhere to land is not a move. Here rather than per
      // emitter, so no piece's own code learns about the card.
      const start = clasped && claspedTo(pos, from) >= 0 ? out.length : -1;

      if (borrowed || crown) {
        emitImitated(pos, from, piece, borrowed, crown, us, out, capturesOnly);
      } else {
        emitFor(pos, from, pieceType(piece), us, out, capturesOnly);
      }
      // **Here rather than in `emitFor`, and once per piece.** That function
      // generates *as if* the piece were `type` and is called twice for a
      // crowned piece, so a row's own steps emitted inside it would be emitted
      // twice — a duplicate move, which `findLegalMove` reads as a promotion.
      emitExtraQuiet(pos, from, us, out, capturesOnly);
      emitLeaps(pos, from, us, out, capturesOnly);

      if (start >= 0) {
        let kept = start;
        for (let i = start; i < out.length; i++) {
          if (claspMove(pos, out[i])) out[kept++] = out[i];
        }
        out.length = kept;
      }
    }
  }

  return out;
}

/**
 * The two passes an imitating piece takes.
 *
 * The quiet half is generated into a scratch list with the borrowed type and
 * filtered, rather than generated directly: an emitter told `capturesOnly` is
 * false still writes captures, and those would be the *wrong* geometry — the
 * whole point is that what it may take is unchanged.
 *
 * **A pawn keeps its promotion**, which is the one thing a naive split loses.
 * Promotion is a property of the square a pawn arrives on rather than of how
 * it walked there (`promotesOn`), so a borrowed step onto the wall is expanded
 * exactly as `generatePawnMoves` expands its own.
 */
function emitImitated(
  pos: Position,
  from: number,
  piece: Piece,
  borrowed: PieceType | 0,
  /** The rank a crowned royal came from: moves *and* takes. See `crownedFrom`. */
  crown: PieceType | 0,
  us: Side,
  out: Move[],
  capturesOnly: boolean
): void {
  const real = pieceType(piece);

  if (!capturesOnly) {
    // **Both patterns, its own first.** Imitate *adds* a way to move and takes
    // none away — a queen who has copied a knight is a queen who can also hop,
    // not a knight. This generated the quiet half from the borrowed type
    // alone, so she lost every one of her own moves: measured on a bare board,
    // thirty-two pseudo-legal moves became nineteen and the only squares she
    // was offered were the eight a knight reaches.
    //
    // Its own moves go in unchanged, which is what keeps a pawn's double step,
    // a king's castling and a pawn's own promotions exactly as they were —
    // none of those survive being re-derived from a borrowed pattern.
    const seen = new Set<number>();
    const keep = (move: Move): void => {
      // **Every attack, not just a capture.** A bash carries `FLAG_BASH` and
      // not `FLAG_CAPTURE`, so testing the one flag let a borrowed pattern
      // shove a piece it was never allowed to touch.
      if (isAttack(move)) return;
      // Two patterns can reach one square — a rook copying a queen shares
      // every rank and file — and the same move twice is a duplicate in every
      // list this feeds, the planner's included.
      const key = move.to * 8 + move.promotion;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(move);
    };

    const own: Move[] = [];
    emitFor(pos, from, real, us, own, false);
    for (const move of own) keep(move);

    const lent: Move[] = [];
    for (const pattern of [borrowed, crown]) {
      if (pattern && pattern !== real) emitFor(pos, from, pattern, us, lent, false);
    }
    for (const move of lent) {
      if (move.flags & FLAG_CAPTURE) continue;
      // **A pawn keeps its promotion**, which is the one thing a borrowed step
      // loses: promotion is a property of the square a pawn arrives on rather
      // than of how it walked there, so a borrowed step onto the wall is
      // expanded exactly as `generatePawnMoves` expands its own.
      if (real === PAWN && promotesOn(pos, us, move.to)) {
        for (const promo of PROMOTION_TYPES) keep(createMove(from, move.to, 0, promo));
      } else {
        keep(move);
      }
    }
  }

  // **A borrowed pattern does not take; a crown does.**
  //
  // That is the half of Imitate which is not about generosity: `attacks.ts`
  // reads the real piece, so a borrowed pattern gives no check, defends no
  // square, and leaves the legality filter's claim about pinned pieces true
  // without being re-argued.
  //
  // A coronation is the other case entirely. She was a queen and is now also
  // royal, so refusing her the capture half left her sliding down a diagonal
  // she could not take on — reported from play as an Empress that moves on her
  // full pattern and attacks on none of it. `attacks.ts` reads the crown too,
  // which is what keeps her checks and her captures the same shape.
  emitFor(pos, from, real, us, out, true);
  if (crown && crown !== real) {
    // Its own list first, so the dedup is against what this piece has already
    // been given rather than against everything on the board — `out` is
    // whatever the caller handed in and may already hold another piece's
    // moves, and it is not always a plain array.
    const mine: Move[] = [];
    emitFor(pos, from, real, us, mine, true);
    const taken: Move[] = [];
    emitFor(pos, from, crown, us, taken, true);
    const seen = new Set<number>();
    for (const move of mine) seen.add(move.to);
    for (const move of taken) {
      if (seen.has(move.to)) continue;
      seen.add(move.to);
      out.push(move);
    }
  }
}

/**
 * May `us` capture the structure standing on `to`? (§26 F5)
 *
 * Enemy structures only — an own or an allied building stays a wall, which is
 * the same line `canTake` draws for pieces. No armour and no king rule: a
 * building is neither.
 */
export function canTakeStructure(pos: Position, to: number, us: Side): boolean {
  const owner = pos.structures.get(to);
  return owner !== undefined && areEnemies(pos, us, owner);
}

/**
 * A move reached an **Obstruction** (§31 S1). An enemy one is attacked where
 * it stands — destroyed, the mover on its square, or a bash that bounces; an
 * own or allied one is simply a wall. `spent`/`path` are a plow on the way
 * (a wall behind a belt), whose HP comes off the blow; a blow with nothing
 * left is not offered. Guarded by `pos.structures.size` at each call site,
 * so a board with no structures pays one comparison and perft is untouched.
 */
function emitFortCapture(
  pos: Position,
  from: number,
  to: number,
  us: Side,
  out: Move[],
  spent = 0,
  path: readonly number[] = NO_BREAK
): void {
  const owner = pos.structures.get(to);
  if (owner === undefined || !areEnemies(pos, us, owner)) return;
  if (blowOf(pos, from) - spent <= 0) return;
  // **Not always a capture** (§27): a building has health, and a blow short of
  // it is a bash that leaves the wall standing. `blowOntoStructure` is the one
  // place that decides, exactly as `blowOnto` is for a piece.
  out.push(blowOntoStructure(pos, from, to, owner, 0, spent, path));
}

/**
 * One square, entered by a step or a jump's landing: a cell, no moat (the
 * caller's tests). A structure there is the new case (§31 S1): an
 * obstruction is attacked or is a wall; an enemy's other kind is crushed by
 * landing — a step pays its HP from the budget, a jump pays nothing; an own
 * one is stood on. A piece standing on a structure is the target as ever.
 */
function emitStep(
  pos: Position,
  from: number,
  to: number,
  us: Side,
  out: Move[],
  capturesOnly: boolean,
  jump: boolean
): void {
  const target = pieceAt(pos, to);
  if (pos.structures.size !== 0 && pos.structures.has(to)) {
    if (obstructsAt(pos, to)) {
      emitFortCapture(pos, from, to, us, out);
      return;
    }
    if (!target) {
      if (capturesOnly) return;
      if (crushes(pos, to, us)) {
        if (!jump && !withinBudget(0, structureHealthAt(pos, to))) return;
        out.push(plowed(createMove(from, to), [to], 0));
      } else {
        out.push(createMove(from, to));
      }
      return;
    }
  }
  if (target ? !canTake(pos, to, target, us) : capturesOnly) return;
  out.push(blowOnto(pos, from, to, target));
}

/**
 * What a quiet, non-jumping walk over `squares` (in order, all empty cells)
 * destroys (§31 S1): `NO_BREAK`, the enemy structures plowed, or null where
 * an obstruction or the budget stops it.
 */
function walkBreaks(pos: Position, squares: readonly number[], us: Side): readonly number[] | null {
  if (pos.structures.size === 0) return NO_BREAK;
  let spent = 0;
  let path: readonly number[] = NO_BREAK;
  for (const sq of squares) {
    if (!pos.structures.has(sq)) continue;
    const cost = enterCost(pos, sq, us);
    if (cost === WALL) return null;
    if (cost === 0) continue;
    if (!withinBudget(spent, cost)) return null;
    spent += cost;
    path = extend(path, sq);
  }
  return path;
}

/** `walkBreaks` for one square. */
const stepBreaks = (pos: Position, to: number, us: Side): readonly number[] | null =>
  pos.structures.size === 0 || !pos.structures.has(to) ? NO_BREAK : walkBreaks(pos, [to], us);

/**
 * A slider's ray (§31 S1's plow). Stops at a piece, a moat, an obstruction
 * (attacked if an enemy's), the range, or the first enemy structure that
 * would take the plow past `RULES.plowBudget`. Every enemy non-obstruction
 * structure crossed is destroyed by every move that crosses it, and a blow
 * at the end loses the HP spent. Own and allied ones are crossed free.
 */
function emitRay(
  pos: Position,
  from: number,
  dir: number,
  range: number,
  us: Side,
  out: Move[],
  capturesOnly: boolean
): void {
  const cells = pos.cells;
  const board = pos.board;
  const forts = pos.structures.size !== 0;
  const moated = pos.moat.size > 0;
  let spent = 0;
  let path: readonly number[] = NO_BREAK;
  let steps = 0;
  for (let to = from + dir; cells.has(to) && steps < range; to += dir) {
    steps++;
    // The ray *stops* at a moat rather than skipping it (§26 C13).
    if (moated && moatLeft(pos, to) > 0) break;
    const target = board.get(to) ?? 0;
    if (forts && pos.structures.has(to)) {
      if (obstructsAt(pos, to)) {
        emitFortCapture(pos, from, to, us, out, spent, path);
        break;
      }
      if (!target) {
        const cost = enterCost(pos, to, us);
        if (cost === WALL) break;
        if (cost > 0) {
          if (!withinBudget(spent, cost)) break;
          spent += cost;
          path = extend(path, to);
        }
        if (!capturesOnly) out.push(plowed(createMove(from, to), path, 0));
        continue;
      }
    }
    if (target) {
      if (canTake(pos, to, target, us) && blowOf(pos, from) - spent > 0) {
        out.push(blowOnto(pos, from, to, target, 0, spent, path));
      }
      break;
    }
    if (!capturesOnly) out.push(path.length > 0 ? plowed(createMove(from, to), path, 0) : createMove(from, to));
  }
}

/**
 * Every move of one piece, generated **as if it were `type`**.
 *
 * The type is a parameter rather than read off the board so that Imitate can
 * ask for a pattern the piece has not got. Everything else about the piece —
 * its facing, its row, its square — is still read from the board,
 * because those belong to the piece and not to the pattern.
 */
function emitFor(
  pos: Position,
  from: number,
  type: PieceType,
  us: Side,
  out: Move[],
  capturesOnly: boolean
): void {
  const board = pos.board;
  const cells = pos.cells;
  const at = (sq: number): number => board.get(sq) ?? 0;
  // Whether any square that is not a cell could be a capture target (§26 F5).
  // Hoisted so every "ran off the board" branch below costs one comparison on
  // the ordinary board.
  const forts = pos.structures.size !== 0;
  // A Static Moat, hoisted the same way — `hasCell` cannot see it (§26 C13).
  const moated = pos.moat.size > 0;

  {
    {
      switch (type) {
        case PAWN:
          generatePawnMoves(pos, from, us, out, capturesOnly);
          break;

        case KNIGHT:
          for (const offset of KNIGHT_DIRS) {
            const to = from + offset;
            // A frozen piece cannot be jumped over (§31 C2).
            if (pos.armour.size > 0 && leapCrossesStasis(pos, from, to)) continue;
            if (!cells.has(to)) continue;
            // A knight may leap the moat, but not land on it (§26 C13).
            if (moated && moatLeft(pos, to) > 0) continue;
            // A jump crosses nothing (§31 S1) and attacks what it lands on.
            if (forts) {
              emitStep(pos, from, to, us, out, capturesOnly, true);
              continue;
            }
            const target = at(to);
            if (target ? !canTake(pos, to, target, us) : capturesOnly) continue;
            out.push(blowOnto(pos, from, to, target));
          }
          break;

        case KING: {
          // **Long Socks: the king slides.** `QUEEN_DIRS` rather than a second
          // loop beside `KING_DIRS`, because a queen's first step in every
          // direction *is* the king's move — one loop covers both and cannot
          // emit a square twice.
          // **And an Artisan Pillar's Monarchs step `ARTISAN.reach` further**
          // (§31 A6): the same slide, `kingReach` squares long.
          const reach = kingReach(pos, us);
          if (reach > 1) {
            for (const dir of QUEEN_DIRS) {
              if (forts) {
                emitRay(pos, from, dir, reach, us, out, capturesOnly);
                continue;
              }
              let steps = 0;
              let to = from + dir;
              for (; cells.has(to) && steps < reach; to += dir) {
                steps++;
                // The ray *stops* at a moat rather than skipping it (§26 C13).
                if (moated && moatLeft(pos, to) > 0) break;
                const target = at(to);
                if (target) {
                  if (canTake(pos, to, target, us)) out.push(blowOnto(pos, from, to, target));
                  break;
                }
                if (!capturesOnly) out.push(moveOnto(from, to, 0));
              }
            }
          } else {
            for (const offset of KING_DIRS) {
              const to = from + offset;
              if (!cells.has(to)) continue;
              if (moated && moatLeft(pos, to) > 0) continue;
              if (forts) {
                emitStep(pos, from, to, us, out, capturesOnly, false);
                continue;
              }
              const target = at(to);
              if (target ? !canTake(pos, to, target, us) : capturesOnly) continue;
              out.push(blowOnto(pos, from, to, target));
            }
          }
          // Castling is never a capture, and it is the one branch here that
          // costs attack queries rather than array pushes — so skipping it is
          // most of what makes the capture pass cheap for a side with rights.
          if (!capturesOnly) generateCastles(pos, us, from, out);
          break;
        }

        default: {
          const dirs =
            type === BISHOP ? BISHOP_DIRS : type === ROOK ? ROOK_DIRS : QUEEN_DIRS;
          // How far it may go. One bound for every slider now, the rook
          // included — see `SLIDER_RANGE`, where the reason is that an
          // uncapped piece is what stopped every attack query from being able
          // to stop early.
          // A row's reach *replaces* its rank's — a turbo row slides further
          // and a Confessor slides less far. `attacks.ts` reads it identically.
          const row = variantRange(variantAt(pos, from));
          const range = row > 0 ? row : SLIDER_RANGE[type];
          for (const dir of dirs) {
            // A board with buildings walks the plow (§31 S1); one without
            // keeps the loop perft has always measured.
            if (forts) {
              emitRay(pos, from, dir, range, us, out, capturesOnly);
              continue;
            }
            let steps = 0;
            let to = from + dir;
            for (; cells.has(to) && steps < range; to += dir) {
              steps++;
              if (moated && moatLeft(pos, to) > 0) break;
              const target = at(to);
              if (target) {
                if (canTake(pos, to, target, us)) out.push(blowOnto(pos, from, to, target));
                break;
              }
              if (!capturesOnly) out.push(createMove(from, to));
            }
          }
        }
      }
    }
  }
}

/**
 * Pawn moves follow the pawn's *facing*, not its colour: it steps one square
 * that way and captures on the two diagonals flanking it.
 *
 * Two things deliberately stay tied to colour rather than facing (PLAN §3.4):
 * promotion happens on the enemy back rank however the pawn is pointing —
 * otherwise a rook could mint south-facing pawns two squares from its own back
 * rank and promote them immediately — and the double step is available only to
 * a pawn still on its starting rank and still pointing the way its side
 * marches, which keeps en passant from becoming a combinatorial swamp.
 */
/**
 * May `us` take what is standing on this square?
 *
 * Not a king. In one-move chess the question never arises — a position where
 * the side to move can take a king is one the opponent was already illegally
 * in check in, and no legal game reaches it — so nothing ever needed to say
 * so. A side that gets two moves in a turn reaches it immediately: give check
 * with the first move and take the king with the second.
 *
 * Filtering here rather than in the legality pass keeps it out of every
 * consumer at once, including the AI's search and the check tests. Perft is
 * unaffected, and `perft.test.ts` is what proves that: the move this removes
 * is unreachable in standard chess.
 *
 * The one place that answers "may this be taken": a rule about alliance,
 * armour and a side's last king restated beside a new command is a rule that
 * drifts.
 */
export function canTake(pos: Position, to: number, target: Piece, us: Side): boolean {
  // An *enemy*, not merely a different side. An allied army — the one The
  // Split gives you — is neither yours nor takeable, and this is the one place
  // that has to know the difference: four capture sites ask `canTake`, and
  // none of them repeat the rule.
  if (target === 0 || !areEnemies(pos, us, pieceColor(target))) return false;
  // **You do not shoot your own transponder** (§26 C12). A piece wearing an
  // army's colours cannot be taken by that army or its allies. Here and not in
  // `canBeTaken`, which is about the piece rather than about who is asking;
  // ahead of the king branch, so a costume covers a royal too. The accessor's
  // own `size` test is what keeps perft unmoved.
  // **That faction alone** (§31 C9): its allies are not fooled.
  const worn = disguisedAs(pos, to);
  if (worn && worn.side === us) return false;
  // **A king that has strayed within reach of another king may be taken.**
  //
  // Two kings can never be adjacent by *moving* — that is what stepping into
  // an attacked square forbids, and it is why chess never needed to say what
  // happens if they are. Belts do not move pieces by moving them: a lane
  // carries a king a square at a time, and the arrangement is reachable.
  //
  // What used to happen was nothing, silently. The carry rolled back on the
  // check the adjacency creates, so the lane simply stopped working with no
  // refusal and no explanation — a king would drift up to the enemy king and
  // park there forever. Now it is a position with an answer in it: whoever is
  // on the clock takes, and taking a king ends that army (`gameStatus`).
  //
  // In `canTake` and deliberately not in `canBeTaken`, which is about the
  // *piece* and is what **Sacrifice** asks. Straying into reach exposes a king
  // to the enemy; it does not make it something its own side may take apart.
  // The exposure exception is about a side's **last** king, which is the only
  // one nothing can otherwise touch. A spare falls through to `canBeTaken` and
  // is captured like any other piece — no adjacency needed, because it is a
  // backup rather than the thing the game ends on.
  if (pieceType(target) === KING && isLastRoyal(pos, pieceColor(target))) {
    return kingIsExposed(pos, to);
  }
  return canBeTaken(pos, to, target);
}

/**
 * Is the king on `at` standing next to a king that is not its ally?
 *
 * The whole of the exception above, and cheap: eight neighbours, asked only
 * when a capture has already found a king on the target square — which in a
 * standard game never happens at all, so perft cannot see this and
 * `perft.test.ts` is what proves it.
 */
export function kingIsExposed(pos: Position, at: number): boolean {
  const side = pieceColor(pieceAt(pos, at));
  for (const offset of KING_DIRS) {
    const near = at + offset;
    if (!hasCell(pos, near)) continue;
    const piece = pieceAt(pos, near);
    if (piece && pieceType(piece) === KING && areEnemies(pos, side, pieceColor(piece))) {
      return true;
    }
  }
  return false;
}

/**
 * Is this piece one that may be removed at all?
 *
 * A king is never takeable, and neither is a piece under a Stasis Field. Split out
 * of `canTake` because there is now a caller that removes a piece **of your
 * own** — Sacrifice — and the enemy test above is specific to *capture*. The
 * two immunities are not: they are about the piece, not about who is asking.
 *
 * One definition either way. Half-applying "a king cannot be removed" is how
 * a game reaches a position with no king in it.
 */
export function canBeTaken(pos: Position, at: number, target: Piece): boolean {
  return canBeHurt(pos, at, target);
}

/**
 * May this piece be *harmed* at all — by a turret, a lance, a blow?
 *
 * The same answer as `canBeTaken` since the monster went (§31 R2); two names
 * because callers mean two things. A king and an armoured piece are refused.
 */
export function canBeHurt(pos: Position, at: number, target: Piece): boolean {
  if (target === 0) return false;
  // **A king is immune only while it is the last one.** The immunity is what
  // makes mate the sole way to finish an army — you cannot take the piece, so
  // you have to run it out of moves. A side holding a spare has not earned
  // that: its kings are backups, so each one is an ordinary target until the
  // count is back to one, and the survivor becomes unkillable again the moment
  // it is alone. Neither is immortal, which is the point of the mechanic.
  if (pieceType(target) === KING && isLastRoyal(pos, pieceColor(target))) return false;
  return armourLeft(pos, at) <= 0;
}

/**
 * A row's `extraQuiet` steps — its rank's pattern plus one square, never a
 * capture.
 *
 * **Nothing here reaches `attacks.ts`, which is the whole shape of the field.**
 * These steps take nothing, so they threaten nothing, so the sweep has no
 * business seeing them and a Confessor's shuffle can never give check or pin.
 * That is why `VARIANT_EXTRA_OFFSETS` is its own table rather than more rows in
 * `VARIANT_OFFSETS`, whose `takes` is exactly what the sweep reads.
 *
 * The moat is asked for the reason every step-mover asks it,
 * and a promotion is expanded only for a **pawn**: `promotesOn` is a fact about
 * the square and the side, so calling it for any rank would promote a bishop.
 */
function emitExtraQuiet(
  pos: Position,
  from: number,
  us: Side,
  out: Move[],
  capturesOnly: boolean
): void {
  if (capturesOnly || pos.variant.size === 0) return;
  const steps = variantExtraSteps(variantAt(pos, from), facingAt(pos, from));
  if (steps.length === 0) return;

  const moated = pos.moat.size > 0;
  const pawn = pieceType(pieceAt(pos, from)) === PAWN;

  for (const offset of steps) {
    const to = from + offset;
    if (!pos.cells.has(to) || pieceAt(pos, to)) continue;
    if (moated && moatLeft(pos, to) > 0) continue;
    // A step through a building's square (§31 S1): wall, crush or stand.
    const broken = stepBreaks(pos, to, us);
    if (broken === null) continue;
    if (pawn && promotesOn(pos, us, to)) {
      for (const promo of PROMOTION_TYPES) out.push(plowed(createMove(from, to, 0, promo), broken, 0));
    } else {
      out.push(plowed(createMove(from, to), broken, 0));
    }
  }
}

/**
 * A row's `leaps` — its rank's pattern plus a longer jump, moving or taking.
 *
 * **Called once per piece beside `emitExtraQuiet`**, for that function's
 * reason: `emitFor` runs twice for a crowned piece, and a leap emitted inside
 * it would be emitted twice. The leaps belong to the *piece* rather than to a
 * rank it is wearing, which is also how `attacks.ts` reads them, so a crowned
 * Pegasus keeps its second ring in both places.
 *
 * The body is the knight's, guard for guard: a leap never traverses what it
 * passes over, so nothing between is asked, but the square it lands on must be
 * a cell and must not be a moat. **No promotion
 * is expanded** — no leaping row is a pawn, pinned in `variants.test.ts`,
 * which is where a row that wanted one would fail.
 */
function emitLeaps(
  pos: Position,
  from: number,
  us: Side,
  out: Move[],
  capturesOnly: boolean
): void {
  if (pos.variant.size === 0) return;
  const leaps = variantLeaps(variantAt(pos, from));
  if (leaps.length === 0) return;

  const cells = pos.cells;
  const forts = pos.structures.size !== 0;
  const moated = pos.moat.size > 0;

  for (const offset of leaps) {
    const to = from + offset;
    if (pos.armour.size > 0 && leapCrossesStasis(pos, from, to)) continue;
    if (!cells.has(to)) continue;
    if (moated && moatLeft(pos, to) > 0) continue;
    // A leap is a jump (§31 S1): it crosses nothing and attacks its landing.
    if (forts) {
      emitStep(pos, from, to, us, out, capturesOnly, true);
      continue;
    }
    const target = pieceAt(pos, to);
    if (target ? !canTake(pos, to, target, us) : capturesOnly) continue;
    out.push(blowOnto(pos, from, to, target));
  }
}

/**
 * Is there really an enemy pawn to take en passant?
 *
 * **An empty square used to answer yes.** The test was
 * `areEnemies(pos, us, pieceColor(pieceAt(pos, victim)))`, and `pieceColor` is
 * `(p >> 3) & 7` — so an empty square, which is 0, reads as *side 0*. To Black
 * that is an enemy, and the capture was offered against nothing.
 *
 * Harmless while `pos.ep` could only ever name a pawn that was still standing
 * there, which was true for as long as the only thing that moved a piece was a
 * move. It stopped being true when pieces could move without one (belts once
 * carried them; a clasp still drags them): the pawn can leave the square
 * between the double push and the answer. The
 * consequence was not a merely illegal move — the move record names the victim
 * `PAWN` explicitly so unmake knows what to put back, so `makeMove` removed
 * nothing and `unmakeMove` **created** a pawn, once per `legalMoves` call.
 *
 * `settleStep` clears the claim at the other end. This is the half that makes
 * the whole class impossible: the victim must be a piece, it must be a pawn,
 * and it must be somebody else's. Perft is unmoved, because in ordinary chess
 * every square `ep` names holds exactly that.
 */
function isEnPassantVictim(pos: Position, victim: number, us: Side): boolean {
  const piece = pieceAt(pos, victim);
  return piece !== 0 && pieceType(piece) === PAWN && areEnemies(pos, us, pieceColor(piece));
}

/**
 * A variant that moves by its own table, rather than as the rank it wears.
 *
 * **Every guard `generatePawnMoves` applies is applied here too** — moat and
 * cell membership are properties of the *square*, so a piece reaching them
 * by another table would walk through a wall everything else respects.
 */
function generateVariantMoves(
  pos: Position,
  from: number,
  us: Side,
  out: Move[],
  capturesOnly: boolean,
  variant: number
): void {
  const cells = pos.cells;
  const facing = facingAt(pos, from);
  const table = VARIANT_OFFSETS[variant][facing];
  const forts = pos.structures.size !== 0;

  const moated = pos.moat.size > 0;
  const moatBlocked = (to: number): boolean => moated && moatLeft(pos, to) > 0;

  /** Emit a capture, or a bash, onto whatever is standing on `to`. */
  const takeAt = (to: number): void => {
    if (!cells.has(to)) return;
    // An obstruction on the square, exactly as a pawn's diagonal takes one.
    if (forts && obstructsAt(pos, to)) {
      if (canTakeStructure(pos, to, us)) {
        const owner = pos.structures.get(to)!;
        const blow = blowOntoStructure(pos, from, to, owner);
        // **A bash never promotes** — the attacker stopped short of the wall.
        if (isBash(blow) || !promotesOn(pos, us, to)) out.push(blow);
        else for (const promo of PROMOTION_TYPES) out.push(moveOntoStructure(from, to, owner, promo));
      }
      return;
    }
    if (moatBlocked(to)) return;
    const target = pieceAt(pos, to);
    if (!target || !canTake(pos, to, target, us)) return;
    if (promotesOn(pos, us, to) && blowKills(pos, from, to)) {
      for (const promo of PROMOTION_TYPES) out.push(blowOnto(pos, from, to, target, promo));
    } else {
      out.push(blowOnto(pos, from, to, target));
    }
  };

  /** Emit a quiet step onto `to`, if it is empty and reachable. */
  const walkTo = (to: number, over: readonly number[] = []): void => {
    if (!cells.has(to) || pieceAt(pos, to) || moatBlocked(to)) return;
    // The plow (§31 S1) over the squares between and the landing.
    const broken = forts ? walkBreaks(pos, [...over, to], us) : NO_BREAK;
    if (broken === null) return;
    if (promotesOn(pos, us, to)) {
      for (const promo of PROMOTION_TYPES) out.push(plowed(createMove(from, to, 0, promo), broken, 0));
    } else {
      out.push(plowed(createMove(from, to), broken, 0));
    }
  };

  // Steps: move onto it if empty, take on it if held. The Marshal's forward is
  // one of these, which is the whole of how it differs from a pawn's push.
  for (const offset of table.steps) {
    const to = from + offset;
    if (!capturesOnly) walkTo(to);
    takeAt(to);
  }

  // Capture-only: the pawn's diagonal, and never a way to advance.
  for (const offset of table.attacks) takeAt(from + offset);

  if (capturesOnly) return;

  // **Move-only, and a step over one square is a slide** — every square between
  // must be empty, so the Marshal's two-forward is a push and not a leap.
  //
  // **No `FLAG_DOUBLE`**: it is available every turn rather than once from the
  // home rank, and `isEnPassantVictim` would put back a `PAWN`.
  const rows = pieceVariant(variant).moves;
  if (!rows) return;
  rows.quiet.forEach(([forward, right], i) => {
    const to = from + table.quiet[i];
    const distance = Math.max(Math.abs(forward), Math.abs(right));
    const over: number[] = [];
    for (let step = 1; step < distance; step++) {
      const between =
        from + stepOffset(facing, (forward * step) / distance, (right * step) / distance);
      if (!cells.has(between) || pieceAt(pos, between) || moatBlocked(between)) {
        return;
      }
      over.push(between);
    }
    walkTo(to, over);
  });
}

function generatePawnMoves(
  pos: Position,
  from: number,
  us: Side,
  out: Move[],
  capturesOnly = false
): void {
  // **A variant moves by its own table**, dispatched here because every caller
  // reaches a pawn through this one door.
  const variant = variantAt(pos, from);
  if (variant && pieceVariant(variant).moves) {
    generateVariantMoves(pos, from, us, out, capturesOnly, variant);
    return;
  }
  const cells = pos.cells;
  const facing = facingAt(pos, from);
  const push = DIR_OFFSET[facing];
  // A pawn steps and takes one square at a time, so "the ray stops" and "this
  // step is refused" are the same thing here — and the double step is gated
  // by the single one it is built on, since `two` is only reached when `one`
  // was legal.
  // A moat refuses a pawn's step (§26 C13).
  const moated = pos.moat.size > 0;
  const moatBlocked = (to: number): boolean => moated && moatLeft(pos, to) > 0;

  const one = from + push;
  // A push through a building (§31 S1): an obstruction stops it, an enemy's
  // other kind is plowed within the budget, an own one is stood on.
  const forts = pos.structures.size !== 0;
  const oneBreaks = forts ? stepBreaks(pos, one, us) : NO_BREAK;
  if (
    !capturesOnly &&
    cells.has(one) &&
    !pieceAt(pos, one) &&
    !moatBlocked(one) &&
    oneBreaks !== null
  ) {
    if (promotesOn(pos, us, one)) {
      for (const promo of PROMOTION_TYPES) out.push(plowed(createMove(from, one, 0, promo), oneBreaks, 0));
    } else {
      out.push(forts ? plowed(createMove(from, one), oneBreaks, 0) : createMove(from, one));
      const two = one + push;
      // No double step in a run (§31 P2), so no en passant either.
      if (
        !pos.runPawns &&
        rankOf(from) === pawnStartRank(us) &&
        facing === forwardFacing(us) &&
        cells.has(two) &&
        !pieceAt(pos, two) &&
        !moatBlocked(two)
      ) {
        const twoBreaks = forts ? walkBreaks(pos, [one, two], us) : NO_BREAK;
        if (twoBreaks !== null) out.push(plowed(createMove(from, two, FLAG_DOUBLE), twoBreaks, 0));
      }
    }
  }

  for (const offset of PAWN_CAPTURES_BY_FACING[facing]) {
    const to = from + offset;
    if (!cells.has(to)) continue;
    // The diagonal lands on an **obstruction** (§31 S1): a pawn takes an enemy
    // one exactly as it takes a piece, promotions included — `promotesOn` is
    // a property of the square it arrives on, however it got there. A pawn's
    // diagonal takes nothing else that is not a piece: other buildings are
    // broken by walking, and the diagonal is not a walk.
    if (forts && obstructsAt(pos, to)) {
      if (canTakeStructure(pos, to, us)) {
        const owner = pos.structures.get(to)!;
        // **A bash never promotes**, the same rule a bash at a piece takes:
        // the pawn stops short of the wall, and a pawn that did not arrive did
        // not arrive. So the four variants are only ever offered for a blow
        // that actually takes the building.
        const blow = blowOntoStructure(pos, from, to, owner);
        if (isBash(blow) || !promotesOn(pos, us, to)) {
          out.push(blow);
        } else {
          for (const promo of PROMOTION_TYPES) {
            out.push(moveOntoStructure(from, to, owner, promo));
          }
        }
      }
      continue;
    }
    if (moatBlocked(to)) continue;

    const target = pieceAt(pos, to);
    if (target && canTake(pos, to, target, us)) {
      // **A bash never promotes**, so it emits one move where a lethal blow
      // emits four: the attacker stops in front of the target rather than
      // reaching its square, and a piece that did not arrive did not arrive.
      // Getting this wrong quadruples the move count for a move that cannot
      // promote.
      if (promotesOn(pos, us, to) && blowKills(pos, from, to)) {
        for (const promo of PROMOTION_TYPES) out.push(blowOnto(pos, from, to, target, promo));
      } else {
        out.push(blowOnto(pos, from, to, target));
      }
    } else if (!target && to === pos.ep && !pos.runPawns && isEnPassantVictim(pos, to - push, us)) {
      // The victim has to be somebody else's. With one move to a turn the
      // check is free — the target square is always an opponent's double push
      // — but a side that moves twice would otherwise be offered an en passant
      // capture of the pawn it had just pushed itself.
      // The victim sits beside `to`, not on it — record PAWN explicitly so
      // unmake knows what to put back and where.
      // The victim sits beside `to`; its side is read off the board rather
      // than assumed to be the mover's opposite.
      out.push(
        createMove(
          from,
          to,
          FLAG_EP | FLAG_CAPTURE,
          0,
          PAWN,
          pieceColor(pieceAt(pos, to - push))
        )
      );
    }
  }
}

function generateCastles(pos: Position, us: Side, from: number, out: Move[]): void {
  const rank = us === WHITE ? 0 : 7;
  const kingFrom = square(4, rank);

  // **The king being generated for, not merely a king of ours.** This body
  // writes a move from the *home* square whatever it was called about, and the
  // caller runs it once per king — so a side holding two would emit the castle
  // twice, from a square one of them is not standing on. Unreachable with one
  // king (moving it clears the rights), free to guard, and Coronation is what
  // makes it reachable.
  if (from !== kingFrom) return;

  // Castling out of check is illegal, and it is cheaper to test once here than
  // per side. Passing *through* check is tested per side below; landing in
  // check is caught by the legality filter.
  if (isAttackedByEnemyOf(pos, kingFrom, us)) return;

  const kingSide = us === WHITE ? CASTLE_WK : CASTLE_BK;
  const queenSide = us === WHITE ? CASTLE_WQ : CASTLE_BQ;

  // A castle walks the rook, and generation only skips a piece at its own `from`.
  const locks = pos.armour.size > 0;
  // Neither the king nor the rook may cross a moat square either (§26 C13).
  const moated = pos.moat.size > 0;
  // Nor a building's square, of any kind (§31 S1): a castle crosses bare ground.
  // An own or allied belt part (§31 S2) is stood on, so it does not stop one.
  const clear = (sq: number): boolean =>
    (!moated || moatLeft(pos, sq) === 0) && noBuildingFor(pos, sq, us);

  // **The squares crossed have to be cells, not merely empty.** A structure's
  // square leaves `cells` while holding no piece, so a store built on f1 after
  // the bishop developed passed the emptiness tests — and the castle walked
  // the king through a wall and parked it on ground that is not board. Found
  // while making structures capturable (§26 F5); on the classic board every
  // square is a cell, so perft cannot see the guard.
  if (
    pos.castling & kingSide &&
    !(locks && armourLeft(pos, square(7, rank)) > 0) &&
    hasCell(pos, square(5, rank)) &&
    hasCell(pos, square(6, rank)) &&
    clear(square(5, rank)) &&
    clear(square(6, rank)) &&
    !pieceAt(pos, square(5, rank)) &&
    !pieceAt(pos, square(6, rank)) &&
    !isAttackedByEnemyOf(pos, square(5, rank), us)
  ) {
    out.push(createMove(kingFrom, square(6, rank), FLAG_CASTLE));
  }

  if (
    pos.castling & queenSide &&
    !(locks && armourLeft(pos, square(0, rank)) > 0) &&
    hasCell(pos, square(3, rank)) &&
    hasCell(pos, square(2, rank)) &&
    hasCell(pos, square(1, rank)) &&
    clear(square(3, rank)) &&
    clear(square(2, rank)) &&
    clear(square(1, rank)) &&
    !pieceAt(pos, square(3, rank)) &&
    !pieceAt(pos, square(2, rank)) &&
    !pieceAt(pos, square(1, rank)) &&
    !isAttackedByEnemyOf(pos, square(3, rank), us)
  ) {
    out.push(createMove(kingFrom, square(2, rank), FLAG_CASTLE));
  }
}

/**
 * Fully legal moves: generate pseudo-legal, then keep only those that don't
 * leave our own king attacked.
 *
 * **The filter is pin-aware**, which is the difference between a move list
 * costing one king-safety query per candidate and costing one per candidate
 * *that could possibly fail*. It used to make and unmake every pseudo-legal
 * move and sweep twelve rays out from the king each time; measured on the
 * opponent's own games that was 15,367 cycles a ply with 89.8% of them
 * proving the move legal, and `isAttackedByEnemyOf` alone was 34% of the
 * planner's entire running time.
 *
 * Four moves still take the expensive path, and each is a real hole in the
 * ordinary theorem rather than caution:
 *
 *  - **anything, while in check** — the king is already attacked, so "this
 *    move does not expose it" is not the question being asked;
 *  - **a king move**, which moves the thing whose safety is in question, and
 *    which castling rides on;
 *  - **an en passant**, which removes a pawn from a square the mover never
 *    stands on and so can open a rank nothing was seen to be blocking;
 *  - **a move by a pinned piece** (`pinnedAgainst`), the whole point.
 *
 * Everything else is legal by construction, and perft is what says so: all 25
 * published node counts have to be unmoved, and they are.
 */
export function generateLegalMoves(
  pos: Position,
  out: Move[] = [],
  capturesOnly = false
): Move[] {
  const us = pos.turn;
  // **The royal whose safety binds, which is not simply "the king".** A side
  // holding a spare is unconstrained — attacking one of two kings is a threat
  // and not a check — so `bindingRoyal` answers -1 and this falls into the
  // no-king branch below. Two royals and none take the same path, which is why
  // the backup rule needed no second filter and no per-royal pass. With
  // exactly one it is `kings[us]`, so every standard position is unmoved and
  // perft cannot see the layer at all.
  const king = bindingRoyal(pos, us);

  // No king on the board — a wave part-built, a test position, a side already
  // beaten. Nothing can be exposed, so nothing needs testing; this is exactly
  // what the old filter did here, since an attack query on a square that does
  // not exist answers false for every move.
  if (king < 0) return generateMoves(pos, out, capturesOnly);

  // **Generate before deciding how to filter.** The check query and the pin
  // sweep are a fixed cost — twelve rays out from the king — paid before a
  // single move is looked at, and with nothing to filter it buys nothing. It
  // was invisible while every call generated a full move list, because a side
  // with a king almost always has a move; `generateLegalCaptures` made it the
  // common case, since most positions offer the opponent no capture at all.
  const moves = generateMoves(pos, [], capturesOnly);
  if (moves.length === 0) return out;

  const inCheck = isAttackedByEnemyOf(pos, king, us);
  // **A spy can check its own king** (§31 C9), and the pin sweep cannot see
  // that, so every move takes the expensive path while a costume is worn.
  const spied = pos.disguise.size > 0;
  // Only worth asking when the answer can be used: in check every move is
  // tested anyway, so the eight rays would be spent for nothing.
  const pinned = inCheck || spied ? EMPTY_PINS : pinnedAgainst(pos, king, us);
  // **A plow is inert to our king when we have one enemy** (§31 S2): outside
  // a bash or a drag, `broken` holds only that enemy's own plowables, which
  // its attackers cross free — so removing them opens no line at us and the
  // pin sweep (plowables transparent) still answers. Obstructions are never
  // in `broken`. A board of belts would otherwise send most moves the slow way.
  const inertPlow = pos.structures.size > 0 && soleEnemy(pos, us);

  for (const move of moves) {
    if (
      !inCheck &&
      !spied &&
      move.from !== king &&
      (move.flags & FLAG_EP) === 0 &&
      // **A bash moves two pieces**, so the pin sweep cannot answer it: the
      // shove can vacate a square the sweep never looked at, and the line it
      // opens may be at our own king. The same exception `FLAG_EP` takes, for
      // the same reason — a piece leaves a square the mover never stands on.
      (move.flags & FLAG_BASH) === 0 &&
      // **A drag moves two pieces**, so the pin sweep cannot answer it either
      // (§26 C11) — and it can block a check as readily as open one.
      !isDrag(move) &&
      // **A move that destroys a building** (§31 S1) can open a line no pin
      // was standing in — a wall gone, a plow under budget again.
      (inertPlow || !breaksStructures(move)) &&
      !isStructureBlow(move) &&
      !pinned.has(move.from)
    ) {
      out.push(move);
      continue;
    }
    const undo = makeMove(pos, move);
    // Re-read rather than reuse `king`: the binding royal may be the piece
    // that just moved. Our own royal *count* cannot change on our own move —
    // nothing captures its own side and no promotion makes a king — so this
    // stays a single square throughout, and the branch above is safe.
    if (!isAttackedByEnemyOf(pos, bindingRoyal(pos, us), us)) out.push(move);
    unmakeMove(pos, move, undo);
  }

  return out;
}

/**
 * The legal *captures* for the side on the clock, and nothing else.
 *
 * **The same list, filtered at the point it is written rather than after.**
 * Everything that asks this question — the opponent's reply search — already
 * discards every non-capture it is handed, and on a grown board that is nearly
 * the whole list: a queen in the open has fifty-odd moves and takes nothing.
 * Measured on a 56x56 board with 94 pieces, the reply search was **67% of the
 * time it took to plan a turn**.
 *
 * Identical answers, and that is a property of *where* the filter sits: the
 * legality pass tests one move at a time, so keeping the captures out of a
 * full list and generating only captures produce the same set. Perft never
 * comes through here and its counts cannot move.
 *
 * **Not memoised**, unlike `legalMoves`. Its only caller hands it a fresh clone
 * per candidate move, so a cache would be a miss every time plus the cost of
 * writing it.
 */
export const generateLegalCaptures = (pos: Position): Move[] =>
  generateLegalMoves(pos, [], true);

/** Is exactly one side an enemy of `us`? */
function soleEnemy(pos: Position, us: Side): boolean {
  let enemies = 0;
  for (let side = 0; side < pos.sides; side++) {
    if (side !== us && areEnemies(pos, us, side) && ++enemies > 1) return false;
  }
  return enemies === 1;
}

/** Allocation-free stand-in for "we did not compute pins". */
const EMPTY_PINS: ReadonlySet<number> = new Set<number>();

/**
 * The legal moves for the side on the clock, **memoised on the position**.
 *
 * Measured, 8.9% of all move generation was one position object being asked
 * twice: `applyMove` resolving its command through `findLegalMove` on a board
 * the planner had just enumerated (half of it), a stance planning on the state
 * the previous stance planned on, and a rollout asking for its own reply.
 *
 * **The returned array is shared and must not be mutated.** Nothing does — every
 * call site reads `.length`, filters, or iterates — and anything wanting a list
 * of its own should call `generateLegalMoves(pos, [])`, which never caches.
 *
 * Invalidation is `invalidateMoves` in every mutator plus `moveStamp` on the
 * scalars; see `Position.moveCache` for why it takes both.
 */
export const legalMoves = (pos: Position): Move[] => {
  const stamp = moveStamp(pos);
  const cached = pos.moveCache;
  if (cached !== undefined && cached.stamp === stamp) return cached.moves;
  const moves = generateLegalMoves(pos);
  pos.moveCache = { stamp, moves };
  return moves;
};
